import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { toast } from 'src/@/components/ui/use-toast';
import { cn } from 'src/@/lib/utils';
import { resolvePlatform } from 'src/design/tokens';
import {
    listSocialCommentsByPost,
    replySocialComment,
    setSocialCommentHidden,
    deleteSocialComment
} from 'src/actions/v2/meta/v2_actionSocialComments';

// Comentarios vivem na colecao social_posts/social_comments, alimentada pelo
// webhook da Meta e chaveada por (platform, post_id) — o MESMO id que o post de
// marketing guarda em external_ids. Por isso da' para reaproveitar a API de
// comentarios do chat aqui sem nada novo no backend.
//
// Estado local de proposito (nao o slice socialComments): aquele slice guarda o
// post selecionado da aba Comentarios do chat; escrever nele daqui deixaria a
// outra tela com um post fantasma selecionado.

const IDFIELD_BY_PLATFORM = { facebook: 'post_id', instagram: 'media_id' };

const AVATAR_COLORS = [
    'bg-violet-600', 'bg-blue-600', 'bg-emerald-600', 'bg-amber-600',
    'bg-rose-600', 'bg-cyan-600', 'bg-indigo-600', 'bg-teal-600',
];

function getAvatarColor(name) {
    if (!name) return AVATAR_COLORS[0];
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function authorName(comment, t) {
    if (comment.isMe) return t('chat:comments.you');
    return comment.from?.name || comment.from?.username || '—';
}

function relativeTime(dateStr, locale) {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    if (Number.isNaN(diff)) return '';
    const mins = Math.round(diff / 60000);
    if (mins < 1) return locale?.startsWith('en') ? 'now' : 'agora';
    if (mins < 60) return `${mins}m`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.round(hours / 24);
    if (days < 7) return `${days}d`;
    return new Date(dateStr).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
}

// Alvos = plataformas em que o post realmente existe na Meta. Um rascunho ainda
// nao publicado nao tem nenhum, e o painel de comentarios nem aparece.
export function commentTargets(post) {
    return (post?.platforms || [])
        .map((platform) => ({
            platform,
            postId: post?.external_ids?.[platform]?.[IDFIELD_BY_PLATFORM[platform]]
        }))
        .filter((target) => Boolean(target.postId));
}

function CommentItem({ comment, isReply, isReplying, onReply, onToggleHide, onDelete, actioningId, confirmingId, onSetConfirming, t, locale }) {
    const nome = authorName(comment, t);
    const isActioning = actioningId === comment.comment_id;
    const isConfirming = confirmingId === comment.comment_id;

    return (
        <div className={cn('flex items-start gap-2', isReply && 'ml-10')}>
            <div className={cn(
                'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-white',
                comment.isMe ? 'bg-violet-500' : getAvatarColor(nome)
            )}>
                {nome.substring(0, 1).toUpperCase()}
            </div>

            <div className="flex-1 min-w-0">
                <div className={cn(
                    'inline-block max-w-full rounded-2xl px-3 py-1.5',
                    comment.isMe ? 'bg-violet-50 dark:bg-violet-500/10' : 'bg-muted',
                    isReplying && 'ring-2 ring-[#7C3AED] ring-offset-1 ring-offset-background',
                    comment.is_hidden && 'opacity-60'
                )}>
                    <div className="flex items-center gap-1.5">
                        <span className="text-[12px] font-semibold text-foreground">{nome}</span>
                        {comment.is_hidden && (
                            <span className="inline-flex items-center gap-0.5 text-[9px] font-medium text-amber-600 dark:text-amber-400">
                                <Iconify icon="eva:eye-off-outline" width={9} />
                                {t('chat:comments.hidden')}
                            </span>
                        )}
                    </div>
                    <p className="text-[13px] text-foreground/90 whitespace-pre-wrap break-words">{comment.text}</p>
                </div>

                {isConfirming ? (
                    <div className="flex items-center gap-2 mt-1 ml-3 text-[11px]">
                        <span className="text-muted-foreground">{t('chat:comments.deleteConfirm')}</span>
                        <button type="button" onClick={() => onDelete(comment)} className="font-semibold text-red-600 hover:underline">
                            {t('chat:comments.delete')}
                        </button>
                        <button type="button" onClick={() => onSetConfirming(null)} className="font-semibold text-muted-foreground hover:underline">
                            {t('common.cancel')}
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center gap-3 mt-0.5 ml-3 text-[11px] text-muted-foreground">
                        {comment.comment_created_time && <span>{relativeTime(comment.comment_created_time, locale)}</span>}
                        {!isReply && (
                            <button
                                type="button"
                                onClick={() => onReply(isReplying ? null : comment)}
                                className={cn('font-semibold hover:underline', isReplying && 'text-[#7C3AED]')}
                            >
                                {t('chat:comments.reply')}
                            </button>
                        )}
                        <button type="button" onClick={() => onToggleHide(comment)} className="font-semibold hover:underline">
                            {comment.is_hidden ? t('chat:comments.unhide') : t('chat:comments.hide')}
                        </button>
                        <button type="button" onClick={() => onSetConfirming(comment.comment_id)} className="font-semibold hover:underline hover:text-red-600">
                            {t('chat:comments.delete')}
                        </button>
                        {isActioning && <Iconify icon="svg-spinners:ring-resize" width={11} />}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function MktPostComments({ post, onCountChange }) {
    const { t, i18n } = useTranslation(['marketing', 'chat']);
    const { user } = useAuth();

    const targets = useMemo(() => commentTargets(post), [post]);
    const [targetIndex, setTargetIndex] = useState(0);
    const target = targets[targetIndex] || targets[0];

    const [comments, setComments] = useState([]);
    // canal_id nao esta no post de marketing — vem do doc de social_posts que a
    // propria listagem devolve. Sem ele nao da' para responder/ocultar/excluir.
    const [canalId, setCanalId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [replyingTo, setReplyingTo] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);
    const [actioningId, setActioningId] = useState(null);
    const [confirmingId, setConfirmingId] = useState(null);

    useEffect(() => {
        setTargetIndex(0);
    }, [post?._id]);

    const carregar = useCallback(async () => {
        if (!target) return;
        setLoading(true);
        try {
            const response = await listSocialCommentsByPost(user.clienteId, target.platform, target.postId);
            const data = response?.data?.data || {};
            setComments(data.comments || []);
            setCanalId(data.post?.canal_id || null);
        } catch (err) {
            setComments([]);
            setCanalId(null);
        } finally {
            setLoading(false);
        }
    }, [user.clienteId, target?.platform, target?.postId]);

    useEffect(() => {
        setReplyingTo(null);
        setReplyText('');
        setConfirmingId(null);
        carregar();
    }, [carregar]);

    useEffect(() => {
        if (onCountChange) onCountChange(comments.length);
    }, [comments.length]);

    const { roots, repliesByParent } = useMemo(() => {
        const rootsList = [];
        const replies = {};
        comments.forEach((c) => {
            if (c.parent_id) {
                if (!replies[c.parent_id]) replies[c.parent_id] = [];
                replies[c.parent_id].push(c);
            } else {
                rootsList.push(c);
            }
        });
        return { roots: rootsList, repliesByParent: replies };
    }, [comments]);

    if (!target) return null;

    const basePayload = {
        cliente_id: user.clienteId,
        canal_id: canalId,
        platform: target.platform,
        post_id: target.postId
    };

    const erroMsg = (err) => err?.response?.data?.mensagem || t('chat:comments.actionError');

    const handleSend = async () => {
        if (!replyText.trim() || !canalId) return;
        setSending(true);
        try {
            const payload = { ...basePayload, message: replyText.trim() };
            if (replyingTo) payload.comment_id = replyingTo.comment_id;
            const response = await replySocialComment(payload);
            const doc = response?.data?.data;
            if (doc) setComments((prev) => (prev.some((c) => c.comment_id === doc.comment_id) ? prev : [...prev, doc]));
            toast({ title: t('chat:comments.replySent') });
            setReplyText('');
            setReplyingTo(null);
        } catch (err) {
            toast({ title: t('chat:comments.actionError'), description: erroMsg(err), variant: 'destructive' });
        } finally {
            setSending(false);
        }
    };

    const handleToggleHide = async (comment) => {
        if (!canalId) return;
        setActioningId(comment.comment_id);
        try {
            await setSocialCommentHidden({ ...basePayload, comment_id: comment.comment_id, hidden: !comment.is_hidden });
            setComments((prev) => prev.map((c) => (
                c.comment_id === comment.comment_id ? { ...c, is_hidden: !comment.is_hidden } : c
            )));
        } catch (err) {
            toast({ title: t('chat:comments.actionError'), description: erroMsg(err), variant: 'destructive' });
        } finally {
            setActioningId(null);
        }
    };

    const handleDelete = async (comment) => {
        if (!canalId) return;
        setConfirmingId(null);
        setActioningId(comment.comment_id);
        try {
            await deleteSocialComment({ ...basePayload, comment_id: comment.comment_id });
            setComments((prev) => prev.filter((c) => c.comment_id !== comment.comment_id && c.parent_id !== comment.comment_id));
            toast({ title: t('chat:comments.deleted') });
        } catch (err) {
            toast({ title: t('chat:comments.actionError'), description: erroMsg(err), variant: 'destructive' });
        } finally {
            setActioningId(null);
        }
    };

    return (
        <div className="flex-1 flex flex-col min-h-0">
            {targets.length > 1 && (
                <div className="flex items-center gap-1 px-5 pt-3 flex-shrink-0">
                    {targets.map((tg, i) => {
                        const info = resolvePlatform(tg.platform);
                        return (
                            <button
                                key={tg.platform}
                                type="button"
                                onClick={() => setTargetIndex(i)}
                                className={cn(
                                    'inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-lg transition-colors',
                                    i === targetIndex ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/60'
                                )}
                            >
                                {info && <Iconify icon={info.icon} width={13} style={{ color: info.color }} />}
                                {info?.label || tg.platform}
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Iconify icon="svg-spinners:ring-resize" width={22} className="text-violet-500" />
                    </div>
                ) : roots.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3 bg-violet-50 dark:bg-violet-500/10">
                            <Iconify icon="eva:message-circle-outline" width={24} className="text-violet-600" />
                        </div>
                        <p className="text-[13px] font-medium text-foreground">{t('chat:comments.emptyComments')}</p>
                        <p className="text-[11px] text-muted-foreground mt-1 max-w-[240px]">{t('posts.comments.syncHint')}</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {roots.map((comment) => (
                            <div key={comment.comment_id} className="flex flex-col gap-2">
                                <CommentItem
                                    comment={comment}
                                    isReply={false}
                                    isReplying={replyingTo?.comment_id === comment.comment_id}
                                    onReply={setReplyingTo}
                                    onToggleHide={handleToggleHide}
                                    onDelete={handleDelete}
                                    actioningId={actioningId}
                                    confirmingId={confirmingId}
                                    onSetConfirming={setConfirmingId}
                                    t={t}
                                    locale={i18n.language}
                                />
                                {(repliesByParent[comment.comment_id] || []).map((reply) => (
                                    <CommentItem
                                        key={reply.comment_id}
                                        comment={reply}
                                        isReply
                                        isReplying={replyingTo?.comment_id === reply.comment_id}
                                        onReply={setReplyingTo}
                                        onToggleHide={handleToggleHide}
                                        onDelete={handleDelete}
                                        actioningId={actioningId}
                                        confirmingId={confirmingId}
                                        onSetConfirming={setConfirmingId}
                                        t={t}
                                        locale={i18n.language}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="border-t border-border px-5 py-3 flex-shrink-0">
                {!loading && !canalId ? (
                    <p className="text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/25 rounded-lg px-3 py-2">
                        {t('posts.comments.noChannel')}
                    </p>
                ) : (
                    <>
                        {replyingTo && (
                            <div className="mb-2">
                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300 text-[11px] font-medium">
                                    <Iconify icon="eva:corner-up-left-outline" width={11} />
                                    {t('chat:comments.replyingTo', { name: authorName(replyingTo, t) })}
                                    <button type="button" onClick={() => setReplyingTo(null)} className="ml-1 hover:text-violet-900">
                                        <Iconify icon="eva:close-outline" width={11} />
                                    </button>
                                </span>
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <input
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                                placeholder={replyingTo ? t('chat:comments.replyPlaceholder') : t('chat:comments.commentPlaceholder')}
                                className="flex-1 h-9 rounded-full border border-border bg-muted/50 px-4 text-[13px] text-foreground focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 focus:outline-none"
                            />
                            <button
                                type="button"
                                onClick={handleSend}
                                disabled={!replyText.trim() || sending}
                                className="w-9 h-9 rounded-full bg-[#7C3AED] hover:bg-[#6d28d9] disabled:opacity-50 text-white flex items-center justify-center flex-shrink-0 transition-colors"
                            >
                                <Iconify icon={sending ? 'svg-spinners:ring-resize' : 'eva:paper-plane-outline'} width={15} />
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
